import { apiGet } from '../../lib/api_v2';

import { messageIdFromString } from '../core/chatItem';
import { cloneRecord, isRecord, stringField } from '../core/record';
import type { ChatItem, MessageId } from '../core/types';
import type { EntitySnapshotMaps } from '../ui/entityBundle';
import {
  enrichRendererReactions,
  profileSnapshot,
  richdownEntitySnapshots
} from '../ui/rendererRowEnrichment';

/** Legacy MessageListItem row shape for the parent message shown above a reply. */
export type ReplyParentRow = Record<string, unknown>;

/** Inputs needed to resolve reply parent rows for visible feed items. */
export interface ReplyResolutionInput {
  readonly channelId: string | null;
  readonly entitySnapshots: EntitySnapshotMaps | null | undefined;
  readonly getLoadedItem: (id: MessageId) => ChatItem | undefined;
  readonly items: readonly ChatItem[];
  readonly markFailed?: (rowId: MessageId, replyId: MessageId) => boolean;
  readonly updateResolved: (rowId: MessageId, reply: ReplyParentRow) => boolean;
}

/** Optional lookup overrides used by tests or alternate feed integrations. */
export interface EchoReplyResolverOptions {
  readonly dexieLookup?: (id: MessageId) => Promise<unknown | null | undefined>;
  readonly networkLookup?: (channelId: string, id: MessageId) => Promise<unknown | null | undefined>;
}

/** Resolves reply preview parent rows for rendered feed items. */
export class EchoReplyResolver {
  private readonly cache = new Map<string, ReplyParentRow>();
  private readonly dexieLookup: (id: MessageId) => Promise<unknown | null | undefined>;
  private readonly failed = new Set<string>();
  private readonly inFlight = new Set<string>();
  private readonly networkLookup: (channelId: string, id: MessageId) => Promise<unknown | null | undefined>;

  constructor(options: EchoReplyResolverOptions = {}) {
    this.dexieLookup = options.dexieLookup ?? defaultDexieLookup;
    this.networkLookup = options.networkLookup ?? defaultNetworkLookup;
  }

  /** Resolves missing reply parents for currently visible items. */
  async resolveVisibleItems(input: ReplyResolutionInput): Promise<void> {
    if (!input.channelId) return;

    const tasks: Promise<void>[] = [];
    for (const item of input.items) {
      const rowId = item.id;
      const replyId = replyTargetId(item);
      if (!rowId || !replyId) continue;
      if (hasTerminalReplyState(item)) continue;

      const key = cacheKey(input.channelId, replyId);
      const cached = this.cache.get(key);
      if (cached) {
        input.updateResolved(rowId, cached);
        continue;
      }
      if (this.failed.has(key) || this.inFlight.has(key)) continue;

      tasks.push(this.resolveOne({
        ...input,
        key,
        replyId,
        rowId
      }));
    }

    await Promise.all(tasks);
  }

  private async resolveOne(input: ReplyResolutionInput & {
    readonly key: string;
    readonly replyId: MessageId;
    readonly rowId: MessageId;
  }): Promise<void> {
    this.inFlight.add(input.key);
    try {
      const loadedItem = input.getLoadedItem(input.replyId);
      if (loadedItem) {
        this.applyResolved(input, rendererRowForReplyItem(loadedItem, input.entitySnapshots));
        return;
      }

      const dexieRow = await this.dexieLookup(input.replyId);
      if (dexieRow) {
        this.applyResolved(input, enrichLegacyReplyRow(dexieRow, input.entitySnapshots));
        return;
      }

      const networkRow = await this.networkLookup(input.channelId!, input.replyId);
      if (networkRow) {
        this.applyResolved(input, enrichLegacyReplyRow(networkRow, input.entitySnapshots));
        return;
      }

      this.markFailed(input);
    } catch {
      this.markFailed(input);
    } finally {
      this.inFlight.delete(input.key);
    }
  }

  private applyResolved(
    input: ReplyResolutionInput & { readonly key: string; readonly replyId: MessageId; readonly rowId: MessageId },
    reply: ReplyParentRow
  ): void {
    this.failed.delete(input.key);
    this.cache.set(input.key, reply);
    input.updateResolved(input.rowId, reply);
  }

  private markFailed(
    input: ReplyResolutionInput & { readonly key: string; readonly replyId: MessageId; readonly rowId: MessageId }
  ): void {
    this.failed.add(input.key);
    input.markFailed?.(input.rowId, input.replyId);
  }
}

function replyTargetId(item: ChatItem): MessageId | null {
  const itemReplyId = isRecord(item) ? stringField(item, 'replyToMessageId') : undefined;
  const rendererReplyId = isRecord(item.rendererRow)
    ? stringField(item.rendererRow, 'reply_to_message_id')
    : undefined;
  const rawId = itemReplyId ?? rendererReplyId;
  return rawId ? messageIdFromString(rawId) ?? null : null;
}

function hasTerminalReplyState(item: ChatItem): boolean {
  if (!isRecord(item.rendererRow)) return false;
  if (isRecord(item.rendererRow.replyToMessage)) return true;
  return item.rendererRow.replyToStatus === 'failed';
}

function rendererRowForReplyItem(
  item: ChatItem,
  snapshots: EntitySnapshotMaps | null | undefined
): ReplyParentRow {
  const base = isRecord(item.rendererRow) ? cloneRecord(item.rendererRow) : {};
  return enrichLegacyReplyRow({
    ...base,
    id: base.id ?? item.id,
    sort_order: base.sort_order ?? item.sortOrder,
    author_id: base.author_id ?? item.author.id,
    author_type: base.author_type ?? 'profile',
    text: base.text ?? item.bodyHtml ?? item.body,
    attachments: base.attachments ?? [],
    reactions: base.reactions ?? {}
  }, snapshots);
}

function enrichLegacyReplyRow(
  row: unknown,
  snapshots: EntitySnapshotMaps | null | undefined
): ReplyParentRow {
  const base = isRecord(row) ? cloneRecord(row) : {};
  const text = stringField(base, 'text') ?? '';
  const authorId = stringField(base, 'author_id');

  return {
    ...base,
    text,
    author: base.author ?? (authorId ? profileSnapshot(snapshots, authorId) : null),
    reactions: enrichRendererReactions(base.reactions, snapshots, {}),
    entity_snapshots: richdownEntitySnapshots(text, snapshots),
    replyToMessage: null,
    replyToStatus: null
  };
}

async function defaultDexieLookup(id: MessageId): Promise<unknown | null | undefined> {
  // Durable server messages live in the feed cache — a parent persisted only in
  // feed_messages must resolve without the network. The legacy messages table stays a
  // transitional compatibility fallback for parents that only ever arrived through
  // legacy paths (bootstrap or live-journal writes for channels the replicator hasn't
  // covered). Loaded lazily so resolver tests (which inject lookups) never touch the
  // Dexie singleton.
  const { resolveMessageForAction } = await import('./messageActions');
  const resolution = await resolveMessageForAction({ messageId: String(id), allowLegacy: true });
  return resolution.state === 'found' ? resolution.message : null;
}

async function defaultNetworkLookup(channelId: string, id: MessageId): Promise<unknown | null | undefined> {
  const response = await apiGet(`/channels/${channelId}/messages/${encodeURIComponent(id)}`);
  const data: unknown = await response.json();
  if (!response.ok || !isRecord(data) || data.ok !== true) return null;
  const envelopeData = isRecord(data.data) ? data.data : null;
  return envelopeData?.message ?? null;
}

function cacheKey(channelId: string, id: MessageId): string {
  return `${channelId}:${id}`;
}
